import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  Avatar
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getUserLeaderboard, getDebateLeaderboard, UserLeaderboardEntry, DebateLeaderboardEntry, PaginationInfo } from '../services/leaderboardService';
import logger from '../utils/logger';

// Add helper function to safely extract user identifier
const getUserIdentifier = (user: UserLeaderboardEntry): string => {
  // Try to get userId first (from our interface)
  if (user.userId) return user.userId;
  
  // Then try common MongoDB id fields that might exist on the object
  const anyUser = user as any;
  if (anyUser._id) return anyUser._id;
  if (anyUser.id) return anyUser.id;
  
  // Fallback to username - we've enhanced the UserProfilePage to handle username lookups
  return user.username;
};

const LeaderboardPage: React.FC = () => {
  const { t } = useTranslation();
  
  // State for tab selection (0 = users, 1 = debates)
  const [tabValue, setTabValue] = useState(0);
  
  // User leaderboard state
  const [userMetric, setUserMetric] = useState('ei');
  const [userPage, setUserPage] = useState(1);
  const [userLeaderboard, setUserLeaderboard] = useState<UserLeaderboardEntry[]>([]);
  const [userPagination, setUserPagination] = useState<PaginationInfo | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  
  // Debate leaderboard state
  const [debateMetric, setDebateMetric] = useState('quality');
  const [debatePage, setDebatePage] = useState(1);
  const [debateLeaderboard, setDebateLeaderboard] = useState<DebateLeaderboardEntry[]>([]);
  const [debatePagination, setDebatePagination] = useState<PaginationInfo | null>(null);
  const [debateLoading, setDebateLoading] = useState(false);
  const [debateError, setDebateError] = useState('');
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Fetch user leaderboard
  const fetchUserLeaderboard = async () => {
    setUserLoading(true);
    setUserError('');
    
    try {
      const data = await getUserLeaderboard(userMetric, userPage, 10);
      setUserLeaderboard(data.leaderboard);
      setUserPagination(data.pagination);
    } catch (error) {
      logger.error('Error fetching user leaderboard:', error);
      setUserError('Failed to load user leaderboard. Please try again later.');
    } finally {
      setUserLoading(false);
    }
  };
  
  // Fetch debate leaderboard
  const fetchDebateLeaderboard = async () => {
    setDebateLoading(true);
    setDebateError('');
    
    try {
      const data = await getDebateLeaderboard(debateMetric, debatePage, 10);
      setDebateLeaderboard(data.leaderboard);
      setDebatePagination(data.pagination);
    } catch (error) {
      logger.error('Error fetching debate leaderboard:', error);
      setDebateError('Failed to load debate leaderboard. Please try again later.');
    } finally {
      setDebateLoading(false);
    }
  };
  
  // Fetch appropriate leaderboard data when tab, metric, or page changes
  useEffect(() => {
    if (tabValue === 0) {
      fetchUserLeaderboard();
    } else {
      fetchDebateLeaderboard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUserLeaderboard/fetchDebateLeaderboard are stable, deps are tab/metric/page
  }, [tabValue, userMetric, userPage, debateMetric, debatePage]);
  
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Format win rate as percentage
  const formatWinRate = (winRate: number) => {
    return `${Math.round(winRate * 100)}%`;
  };
  
  // Render user leaderboard table
  const renderUserLeaderboard = () => {
    if (userLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (userError) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>{userError}</Alert>
      );
    }
    
    if (userLeaderboard.length === 0) {
      return (
        <Alert severity="info" sx={{ my: 2 }}>
          {t('leaderboard.noUsersFound')}
        </Alert>
      );
    }
    
    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.rank')}</TableCell>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.user')}</TableCell>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.publicDebates')}</TableCell>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.wins')}</TableCell>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.winRate')}</TableCell>
              {userMetric === 'ei' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.excaliburIndex')}</TableCell>
              )}
              {userMetric === 'explanationStrength' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.explanationStrength')}</TableCell>
              )}
              {userMetric === 'argumentClarity' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.argumentClarity')}</TableCell>
              )}
              {userMetric === 'rebuttalEffectiveness' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.rebuttalEffectiveness')}</TableCell>
              )}
              {userMetric === 'evidenceQuality' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.evidenceQuality')}</TableCell>
              )}
              {userMetric === 'steelmanningRating' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.steelmanningRating')}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {userLeaderboard.map((user) => (
              <TableRow key={user.username} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                <TableCell>
                  <Chip 
                    label={user.rank} 
                    color={user.rank <= 3 ? 'primary' : 'default'} 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box 
                    component={RouterLink} 
                    to={`/user/${getUserIdentifier(user)}`} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <Avatar sx={{ width: 24, height: 24, mr: 1, backgroundColor: 'primary.main' }}>
                      {user.username.charAt(0).toUpperCase()}
                    </Avatar>
                    {user.username}
                  </Box>
                </TableCell>
                <TableCell>{user.publicDebates}</TableCell>
                <TableCell>{user.publicWins}</TableCell>
                <TableCell>{formatWinRate(user.winRate)}</TableCell>
                {userMetric === 'ei' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(user.excaliburIndex * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {userMetric === 'explanationStrength' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(user.stats.explanationStrength * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {userMetric === 'argumentClarity' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(user.stats.argumentClarity * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {userMetric === 'rebuttalEffectiveness' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(user.stats.rebuttalEffectiveness * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {userMetric === 'evidenceQuality' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(user.stats.evidenceQuality * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {userMetric === 'steelmanningRating' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(user.stats.steelmanningRating * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Render debate leaderboard table
  const renderDebateLeaderboard = () => {
    if (debateLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (debateError) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>{debateError}</Alert>
      );
    }
    
    if (debateLeaderboard.length === 0) {
      return (
        <Alert severity="info" sx={{ my: 2 }}>
          {t('leaderboard.noDebatesFound')}
        </Alert>
      );
    }
    
    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.rank')}</TableCell>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.topic')}</TableCell>
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.teams')}</TableCell>
              {debateMetric === 'quality' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.overallQuality')}</TableCell>
              )}
              {debateMetric === 'explanationQuality' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.explanationQuality')}</TableCell>
              )}
              {debateMetric === 'logicalCoherence' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.logicalCoherence')}</TableCell>
              )}
              {debateMetric === 'evidenceQuality' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.evidenceQuality')}</TableCell>
              )}
              {debateMetric === 'civilDiscourse' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.civilDiscourse')}</TableCell>
              )}
              {debateMetric === 'messages' && (
                <TableCell sx={{ color: 'white' }}>{t('leaderboard.messageCount')}</TableCell>
              )}
              <TableCell sx={{ color: 'white' }}>{t('leaderboard.date')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {debateLeaderboard.map((debate) => (
              <TableRow 
                key={debate.id} 
                sx={{ 
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                  cursor: 'pointer'
                }}
                onClick={() => {
                  window.location.href = `/debate/view/${debate.id}`;
                }}
                style={{ textDecoration: 'none' }}
              >
                <TableCell>
                  <Chip 
                    label={debate.rank} 
                    color={debate.rank <= 3 ? 'primary' : 'default'} 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {debate.topic}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    {debate.teams.map((team, index) => (
                      <Chip 
                        key={team.id}
                        label={team.name}
                        size="small"
                        color={index === 0 ? 'primary' : 'secondary'}
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </TableCell>
                {debateMetric === 'quality' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(debate.metrics.overallQuality * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {debateMetric === 'explanationQuality' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(debate.metrics.explanationQuality * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {debateMetric === 'logicalCoherence' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(debate.metrics.logicalCoherence * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {debateMetric === 'evidenceQuality' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(debate.metrics.evidenceQuality * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {debateMetric === 'civilDiscourse' && (
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium" 
                      color="primary"
                    >
                      {(debate.metrics.civilDiscourse * 10).toFixed(1)}
                    </Typography>
                  </TableCell>
                )}
                {debateMetric === 'messages' && (
                  <TableCell>{debate.messageCount}</TableCell>
                )}
                <TableCell>{formatDate(debate.endDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('leaderboard.leaderboards')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('leaderboard.viewTopPerformers')}
        </Typography>
        
        {/* Tab navigation */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label={t('leaderboard.topDebaters')} />
          <Tab label={t('leaderboard.topDebates')} />
        </Tabs>
        
        {/* User Leaderboard Tab */}
        {tabValue === 0 && (
          <Box>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" component="h2">
                  {t('leaderboard.topDebaters')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="user-metric-label">{t('leaderboard.rankingBy')}</InputLabel>
                  <Select
                    labelId="user-metric-label"
                    value={userMetric}
                    label={t('leaderboard.rankingBy')}
                    onChange={(e) => {
                      setUserMetric(e.target.value);
                      setUserPage(1);
                    }}
                  >
                    <MenuItem value="ei">{t('leaderboard.excaliburIndex')}</MenuItem>
                    <MenuItem value="wins">{t('leaderboard.wins')}</MenuItem>
                    <MenuItem value="winRate">{t('leaderboard.winRate')}</MenuItem>
                    <MenuItem value="explanationStrength">{t('leaderboard.explanationStrength')}</MenuItem>
                    <MenuItem value="argumentClarity">{t('leaderboard.argumentClarity')}</MenuItem>
                    <MenuItem value="rebuttalEffectiveness">{t('leaderboard.rebuttalEffectiveness')}</MenuItem>
                    <MenuItem value="evidenceQuality">{t('leaderboard.evidenceQuality')}</MenuItem>
                    <MenuItem value="steelmanningRating">{t('leaderboard.steelmanningRating')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {renderUserLeaderboard()}
            
            {/* Pagination for users */}
            {userPagination && userPagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={userPagination.pages}
                  page={userPage}
                  onChange={(event, value) => setUserPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
        
        {/* Debate Leaderboard Tab */}
        {tabValue === 1 && (
          <Box>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" component="h2">
                  {t('leaderboard.topDebates')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="debate-metric-label">{t('leaderboard.rankingBy')}</InputLabel>
                  <Select
                    labelId="debate-metric-label"
                    value={debateMetric}
                    label={t('leaderboard.rankingBy')}
                    onChange={(e) => {
                      setDebateMetric(e.target.value);
                      setDebatePage(1);
                    }}
                  >
                    <MenuItem value="quality">{t('leaderboard.overallQuality')}</MenuItem>
                    <MenuItem value="explanationQuality">{t('leaderboard.explanationQuality')}</MenuItem>
                    <MenuItem value="logicalCoherence">{t('leaderboard.logicalCoherence')}</MenuItem>
                    <MenuItem value="evidenceQuality">{t('leaderboard.evidenceQuality')}</MenuItem>
                    <MenuItem value="civilDiscourse">{t('leaderboard.civilDiscourse')}</MenuItem>
                    <MenuItem value="messages">{t('leaderboard.messageCount')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {renderDebateLeaderboard()}
            
            {/* Pagination for debates */}
            {debatePagination && debatePagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={debatePagination.pages}
                  page={debatePage}
                  onChange={(event, value) => setDebatePage(value)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default LeaderboardPage; 